/**
 * Chunk lifecycle manager: generation, lighting, meshing, upload, unload.
 *
 * Pipeline (per chunk column):
 *   GEN + local light (worker)  ->  STITCH light (main, time-sliced)  ->  MESH (worker)  ->  UPLOAD (budgeted)
 *
 * A chunk's light is stitched once its 4 horizontal neighbors have voxel data
 * (so light crosses borders), and it is meshed once all 8 neighbors are lit
 * (so AO + smooth light at borders are seamless). Work is handed to workers
 * nearest-first and only while one is idle, so nothing queues up behind the
 * player. Chunks beyond the unload radius are disposed.
 *
 * Remote mode (the game server owns the world, docs/server-model.md): nothing
 * is generated here. Chunks arrive through receiveChunk(), are lit locally in
 * a worker and then take the same stitch -> mesh -> upload path; the server
 * says when to drop one (unloadChunk). Server facts go in through applyRemote(),
 * the player's own predicted edits through predictBlock(). No WorldSave.
 */

import * as THREE from 'three';
import { WORLD_HEIGHT, CHUNK_SIZE } from '../constants';
import { Chunk, ChunkState, World, worldKey, keyCx, keyCz, CHUNK_AREA } from './chunk';
import { stitchChunk, lightChunkLocal, relightAfterEdit } from './lighting';
import { IS_SOLID, NEEDS_SUPPORT } from '../blocks';
import { editBlock, type EditHooks } from './edit';
import { buildPadded } from './padded';
import { WorkerPool, type GenResponse, type MeshResponse } from '../workers/pool';
import type { MeshResult, PassMesh } from '../mesher/greedy';
import { reshapeChunkBorder } from './reshape';
import type { WorldSave } from './save';
import type { DimensionId } from './dimension';
import { ChunkBatcher, type BatchKind } from '../render/chunkBatch';

export interface ChunkMaterials {
  opaque: THREE.Material;
  cutout: THREE.Material;
  translucent: THREE.Material;
  /** Depth-only twins used for the sun shadow pass. */
  depthOpaque: THREE.Material;
  depthCutout: THREE.Material;
}

/** Main-thread ms per frame spent stitching light across chunk borders. */
const LIGHT_BUDGET_MS = 4;
const MAX_UPLOADS_PER_FRAME = 8;
/** Per-frame GL upload budget when chunk meshes are region-batched. */
const UPLOAD_BUDGET_MS = 1.5;
/** Chunks kept beyond the generation radius before unloading (hysteresis). */
const UNLOAD_MARGIN = 2;
/**
 * Remote mode: once no chunk frame has arrived for this long, the server has
 * sent what it is going to, and a chunk stops waiting for missing neighbours
 * (the server only sends inside its view disc, so edge chunks never get them).
 */
const REMOTE_QUIET_MS = 350;

interface Offset {
  dx: number;
  dz: number;
  d2: number;
  /** This chunk should be lit (it, or a neighbour, gets meshed). */
  lit: boolean;
  /** This chunk should be meshed (inside the view disc). */
  mesh: boolean;
}

/**
 * Chunk offsets to keep loaded, nearest first. The view disc is meshed; its
 * 8-neighbourhood must be lit; the 4-neighbourhood of that must be generated.
 */
function buildOffsets(viewRadius: number): Offset[] {
  const r = viewRadius + 2;
  const w = r * 2 + 1;
  const mesh = new Uint8Array(w * w);
  const lit = new Uint8Array(w * w);
  const gen = new Uint8Array(w * w);
  const at = (dx: number, dz: number) => dx + r + (dz + r) * w;
  const limit = (viewRadius + 0.5) * (viewRadius + 0.5);
  for (let dz = -viewRadius; dz <= viewRadius; dz++) {
    for (let dx = -viewRadius; dx <= viewRadius; dx++) {
      if (dx * dx + dz * dz > limit) continue;
      mesh[at(dx, dz)] = 1;
      for (let nz = -1; nz <= 1; nz++) {
        for (let nx = -1; nx <= 1; nx++) lit[at(dx + nx, dz + nz)] = 1;
      }
    }
  }
  for (let dz = -r + 1; dz < r; dz++) {
    for (let dx = -r + 1; dx < r; dx++) {
      if (!lit[at(dx, dz)]) continue;
      gen[at(dx, dz)] = gen[at(dx + 1, dz)] = gen[at(dx - 1, dz)] = gen[at(dx, dz + 1)] = gen[at(dx, dz - 1)] = 1;
    }
  }
  const out: Offset[] = [];
  for (let dz = -r; dz <= r; dz++) {
    for (let dx = -r; dx <= r; dx++) {
      if (!gen[at(dx, dz)]) continue;
      out.push({ dx, dz, d2: dx * dx + dz * dz, lit: lit[at(dx, dz)] === 1, mesh: mesh[at(dx, dz)] === 1 });
    }
  }
  out.sort((a, b) => a.d2 - b.d2);
  return out;
}

/** Static subtree: world matrices are written once when a mesh is added. */
function noopMatrixUpdate(): void { /* intentionally empty */ }

export class ChunkManager {
  readonly world: World;
  readonly group = new THREE.Group();
  /** Depth-only twins of the opaque + cutout meshes, for the shadow scene. */
  readonly shadowGroup = new THREE.Group();
  /** Bumped whenever chunk geometry is added or removed (shadow cache key). */
  version = 0;

  private readonly pool: WorkerPool;
  private offsets: Offset[] = [];
  private unloadRadius = 0;
  private pcx = 0;
  private pcz = 0;
  private hasCenter = false;

  /** Chunks with light/mesh changes from edits that need a remesh first. */
  private readonly remeshQueue = new Set<number>();
  private readonly pendingGen = new Set<number>();
  private readonly pendingMesh = new Set<number>();
  private readonly pendingUploads: Array<{ cx: number; cz: number; result: MeshResult }> = [];
  private readonly meshes = new Map<number, THREE.Mesh[]>();
  private readonly lightDirty = new Set<number>();
  /** Nothing left to schedule until the next event (move, edit, job result). */
  private settled = false;
  /** Inside batchEdits(): remeshes are kicked off once at the end. */
  private batching = false;

  // --- remote mode (see the file header) ---
  /** Chunks received from the server, waiting for an idle worker to light them. */
  private readonly remoteQueue = new Map<number, Uint16Array>();
  /** Block changes [index, id, ...] for a chunk whose light job is in flight. */
  private readonly remoteStash = new Map<number, number[]>();
  /** Chunks unloaded while their light job was in flight: the result is dropped. */
  private readonly remoteCancel = new Set<number>();
  /** Chunks meshed while a neighbour was missing: remeshed when one arrives. */
  private readonly partial = new Set<number>();
  private lastChunkAt = 0;
  /** predictBlock(): [x, y, z, oldId, ...] of the cells the edit may touch. */
  private capture: number[] | null = null;
  /**
   * Remote: where the game's own setBlock() calls go instead of straight into
   * the world. The receiver turns the edit into an intent for the server and
   * applies it through predictBlock(). Null = setBlock() edits locally.
   */
  sendEdit: ((wx: number, wy: number, wz: number, id: number) => void) | null = null;

  /** Dimension being generated; see setDimension(). */
  dimension: DimensionId = 'overworld';
  /** Bumped on a dimension switch: worker results tagged with an older epoch are dropped. */
  private epoch = 0;

  /** Called for every block that changed through setBlock (incl. unsupported blocks it broke). */
  onBlockChange: ((wx: number, wy: number, wz: number) => void) | null = null;
  /** A supported block (flora, crop, torch...) popped off because its support went or water washed it
   *  away: vanilla drops its loot (as if broken by hand). Not called for the block setBlock itself replaced by hand. */
  onPopped: ((wx: number, wy: number, wz: number, oldId: number) => void) | null = null;
  private readonly editHooks: EditHooks = {
    record: (cx, cz, index, id) => { this.save?.record(cx, cz, index, id); },
    setBlock: (x, y, z, id) => { this.setBlock(x, y, z, id); },
    popped: (x, y, z, oldId) => { this.onPopped?.(x, y, z, oldId); },
    railBusy: false,
  };

  stats = {
    chunks: 0, meshJobs: 0, uploads: 0, genJobs: 0,
    pendingGen: 0, pendingMesh: 0, pendingUploads: 0,
    /** Smoothed worker ms per job. */
    genMs: 0, meshMs: 0,
    /** Chunks inside the view disc that are meshed, and the disc size. */
    ready: 0, wanted: 0,
  };

  constructor(
    seed: number, private readonly materials: ChunkMaterials, viewRadius: number,
    private readonly save?: WorldSave,
    initialBiomeBlend: 0 | 1 | 2 = 2,
    /** The game server owns the world: never generate, never touch a WorldSave. */
    readonly remote = false,
  ) {
    if (remote && save) throw new Error('a remote ChunkManager takes no WorldSave');
    this.world = new World(seed);
    // The chunk groups never move. With matrixAutoUpdate on, every frame's
    // updateMatrix() flags matrixWorldNeedsUpdate and force-recomputes the
    // world matrix of every chunk mesh below (~1000 multiplies per frame).
    this.group.matrixAutoUpdate = false;
    this.shadowGroup.matrixAutoUpdate = false;
    // ...and every child mesh sets its world matrix once at upload, so the
    // per-frame scene traversal can skip these subtrees entirely.
    this.group.updateMatrixWorld = noopMatrixUpdate;
    this.shadowGroup.updateMatrixWorld = noopMatrixUpdate;
    this.pool = new WorkerPool((msg) => this.onWorkerMessage(msg));
    this.setViewRadius(viewRadius);
    // tell the worker the initial biome-blend radius from the quality preset
    this.pool.broadcast({ type: 'config', biomeBlend: initialBiomeBlend });
  }

  setViewRadius(viewRadius: number): void {
    this.offsets = buildOffsets(viewRadius);
    this.unloadRadius = viewRadius + 2 + UNLOAD_MARGIN;
    this.stats.wanted = 0;
    for (const o of this.offsets) if (o.mesh) this.stats.wanted++;
    if (this.hasCenter) this.unloadFar();
    this.settled = false;
  }

  /** Push runtime config (biome blend, etc.) to every worker. */
  broadcastConfig(cfg: { biomeBlend: 0 | 1 | 2 }): void {
    this.pool.broadcast({ type: 'config', biomeBlend: cfg.biomeBlend });
  }

  private onWorkerMessage(msg: GenResponse | MeshResponse): void {
    const key = worldKey(msg.cx, msg.cz);
    this.settled = false;
    if ((msg.epoch ?? 0) !== this.epoch) { this.schedule(false); return; } // from before a dimension switch
    if (msg.type === 'gen') {
      this.pendingGen.delete(key);
      this.stats.genJobs++;
      this.stats.genMs = this.stats.genMs === 0 ? msg.ms : this.stats.genMs * 0.9 + msg.ms * 0.1;
      if (this.remote) {
        if (this.remoteCancel.delete(key) || this.remoteQueue.has(key)) { this.remoteStash.delete(key); this.schedule(false); return; } // unloaded or resent meanwhile
        const stash = this.remoteStash.get(key);
        if (stash) {
          // the server changed blocks while the worker held the chunk: apply and light again
          this.remoteStash.delete(key);
          for (let i = 0; i < stash.length; i += 2) {
            msg.solidCount += IS_SOLID[stash[i + 1]] - IS_SOLID[msg.blocks[stash[i]]];
            msg.blocks[stash[i]] = stash[i + 1];
          }
          msg.light.fill(0);
          lightChunkLocal(msg.blocks, msg.light);
        }
      } else if (!this.inRange(msg.cx, msg.cz) || this.world.getChunk(msg.cx, msg.cz)) return; // stale
      const chunk = new Chunk(msg.cx, msg.cz, msg.blocks, msg.light);
      chunk.solidCount = msg.solidCount;
      chunk.state = ChunkState.Generated;
      this.world.setChunk(chunk);
    } else {
      this.pendingMesh.delete(key);
      this.stats.meshJobs++;
      this.stats.meshMs = this.stats.meshMs === 0 ? msg.ms : this.stats.meshMs * 0.9 + msg.ms * 0.1;
      if (this.world.getChunk(msg.cx, msg.cz)) {
        this.pendingUploads.push({ cx: msg.cx, cz: msg.cz, result: msg.result });
      }
    }
    // keep the freed worker busy without waiting for the next frame
    this.schedule(false);
  }

  private inRange(cx: number, cz: number): boolean {
    return Math.abs(cx - this.pcx) <= this.unloadRadius && Math.abs(cz - this.pcz) <= this.unloadRadius;
  }

  /** Per-frame pump. Call with the player's world position. */
  update(px: number, pz: number): void {
    const pcx = Math.floor(px / CHUNK_SIZE);
    const pcz = Math.floor(pz / CHUNK_SIZE);
    if (!this.hasCenter || pcx !== this.pcx || pcz !== this.pcz) {
      this.pcx = pcx;
      this.pcz = pcz;
      this.hasCenter = true;
      this.settled = false;
      this.unloadFar();
    }

    if (!this.settled) this.schedule(true);

    // Upload finished meshes (budgeted per frame).
    let uploads = 0;
    const uploadStart = performance.now();
    while (this.pendingUploads.length > 0 && uploads < MAX_UPLOADS_PER_FRAME) {
      // batched uploads cost GL time here (not lazily in render): time-box them
      if (this.batcher && uploads > 0 && performance.now() - uploadStart > UPLOAD_BUDGET_MS) break;
      const { cx, cz, result } = this.pendingUploads.shift()!;
      const chunk = this.world.getChunk(cx, cz);
      if (chunk && chunk.state >= ChunkState.Lit) {
        this.uploadMeshes(cx, cz, result);
        const first = chunk.state !== ChunkState.Ready;
        chunk.state = ChunkState.Ready;
        if (first) this.wakeEdits(chunk);
        uploads++;
        this.stats.uploads++;
      }
    }

    this.stats.chunks = this.world.chunks.size;
    this.stats.pendingGen = this.pendingGen.size;
    this.stats.pendingMesh = this.pendingMesh.size;
    this.stats.pendingUploads = this.pendingUploads.length;
  }

  /**
   * Hand out work nearest-first: gen/mesh jobs to idle workers and (when
   * `allowLight`) border light stitching on this thread within its budget.
   */
  private schedule(allowLight: boolean): void {
    const t0 = allowLight ? performance.now() : 0;
    let lightOk = allowLight;
    let blocked = false; // work exists that could not be started this pass

    // Re-mesh dirty (edited / re-lit) chunks — highest priority.
    for (const key of this.remeshQueue) {
      const chunk = this.world.chunks.get(key);
      if (!chunk || !chunk.meshDirty) {
        this.remeshQueue.delete(key);
        continue;
      }
      if (chunk.state < ChunkState.Lit || this.pendingMesh.has(key)) { blocked = true; continue; }
      if (!this.neighborsAtLeast(chunk.cx, chunk.cz, ChunkState.Lit, true)) { blocked = true; continue; }
      if (!this.postMeshJob(chunk)) { blocked = true; break; }
      this.remeshQueue.delete(key);
    }

    // Remote: light the chunks the server sent (it sends nearest first, and the map keeps that order).
    for (const [key, blocks] of this.remoteQueue) {
      if (this.pendingGen.has(key)) continue; // an older copy is still in a worker
      if (!this.pool.run({ type: 'light', cx: keyCx(key), cz: keyCz(key), blocks, epoch: this.epoch }, [blocks.buffer])) { blocked = true; break; }
      this.remoteQueue.delete(key);
      this.pendingGen.add(key);
    }

    let ready = 0;
    let held = 0;
    for (const off of this.offsets) {
      const cx = this.pcx + off.dx;
      const cz = this.pcz + off.dz;
      const chunk = this.world.getChunk(cx, cz);
      if (chunk && off.mesh) held++;
      if (!chunk) {
        if (this.remote) continue; // the server decides which chunks exist
        const key = worldKey(cx, cz);
        if (this.pendingGen.has(key)) continue;
        if (this.pool.run({ type: 'gen', cx, cz, edits: this.save?.editsFor(cx, cz), dim: this.dimension, epoch: this.epoch })) this.pendingGen.add(key);
        else blocked = true;
        continue;
      }
      if (chunk.state === ChunkState.Generated) {
        if (!off.lit) continue;
        if (!this.neighborsAtLeast(cx, cz, ChunkState.Generated, false)) { if (this.remote) blocked = true; continue; }
        if (!lightOk) { blocked = true; continue; }
        this.lightDirty.clear();
        reshapeChunkBorder(chunk.blocks, cx, cz, (x, y, z) => this.world.getBlock(x, y, z));
        stitchChunk(this.world, chunk, this.lightDirty);
        chunk.state = ChunkState.Lit;
        // light that flowed into already-meshed neighbours needs a remesh
        for (const k of this.lightDirty) {
          const c = this.world.chunks.get(k);
          if (c && c.state === ChunkState.Ready) this.remeshQueue.add(k);
        }
        if (this.remote && this.partial.size > 0) this.remeshPartialAround(cx, cz);
        if (performance.now() - t0 > LIGHT_BUDGET_MS) lightOk = false;
        blocked = true; // newly lit chunks unlock meshing — scan again next frame
        continue;
      }
      if (!off.mesh) continue;
      if (chunk.state === ChunkState.Ready && !chunk.meshDirty) { ready++; continue; }
      if (!chunk.meshDirty) continue; // meshed, upload pending
      if (this.pendingMesh.has(worldKey(cx, cz))) continue;
      if (!this.neighborsAtLeast(cx, cz, ChunkState.Lit, true)) { if (this.remote) blocked = true; continue; }
      if (!this.postMeshJob(chunk)) blocked = true;
    }

    this.stats.ready = ready;
    if (this.remote) this.stats.wanted = held; // what the server has sent of our view
    if (allowLight && !blocked && this.pendingGen.size === 0 && this.pendingMesh.size === 0
      && this.pendingUploads.length === 0 && this.remeshQueue.size === 0) {
      this.settled = true;
    }
  }

  /** Are the 4 (or 8 with `diagonals`) neighbours at least in `state`? */
  private neighborsAtLeast(cx: number, cz: number, state: ChunkState, diagonals: boolean): boolean {
    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dz === 0) continue;
        if (!diagonals && dx !== 0 && dz !== 0) continue;
        const c = this.world.getChunk(cx + dx, cz + dz);
        if (!c && this.remote && this.neverComing(worldKey(cx + dx, cz + dz))) continue;
        if (!c || c.state < state) return false;
      }
    }
    return true;
  }

  /** Remote: a missing chunk that is not on its way (the server's stream has gone quiet). */
  private neverComing(key: number): boolean {
    return !this.remoteQueue.has(key) && !this.pendingGen.has(key) && performance.now() - this.lastChunkAt > REMOTE_QUIET_MS;
  }

  /** Remote: a chunk at (cx, cz) just got its light; neighbours meshed without it need another pass. */
  private remeshPartialAround(cx: number, cz: number): void {
    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) {
        const k = worldKey(cx + dx, cz + dz);
        if (!this.partial.has(k)) continue;
        const c = this.world.chunks.get(k);
        if (!c) { this.partial.delete(k); continue; }
        c.meshDirty = true;
        if (c.state === ChunkState.Ready) this.remeshQueue.add(k);
      }
    }
  }

  private postMeshJob(chunk: Chunk): boolean {
    if (this.pool.idle === 0) return false;
    const padded = buildPadded(this.world, chunk.cx, chunk.cz);
    this.pool.run(
      { type: 'mesh', cx: chunk.cx, cz: chunk.cz, blocks: padded.blocks, light: padded.light, epoch: this.epoch },
      [padded.blocks.buffer, padded.light.buffer]
    );
    chunk.meshDirty = false;
    const key = worldKey(chunk.cx, chunk.cz);
    this.pendingMesh.add(key);
    if (this.remote) {
      let all = true;
      for (let dz = -1; dz <= 1 && all; dz++) for (let dx = -1; dx <= 1; dx++) if (!this.world.getChunk(chunk.cx + dx, chunk.cz + dz)) { all = false; break; }
      if (all) this.partial.delete(key); else this.partial.add(key);
    }
    return true;
  }

  private batcher: ChunkBatcher | null = null;
  private batchKinds: ReadonlySet<BatchKind> = new Set();

  /**
   * Route chunk passes through region batches (render/chunkBatch.ts) instead
   * of one mesh per chunk per pass. Call before any chunk is uploaded.
   */
  enableBatching(gl: WebGL2RenderingContext, translucent = false): void {
    this.batchKinds = new Set<BatchKind>(translucent ? ['opaque', 'cutout', 'translucent'] : ['opaque', 'cutout']);
    this.batcher = new ChunkBatcher(gl, {
      group: this.group,
      shadowGroup: this.shadowGroup,
      material: (k) => this.materials[k],
      depthMaterial: (k) => k === 'opaque' ? this.materials.depthOpaque : k === 'cutout' ? this.materials.depthCutout : null,
    });
  }

  private uploadMeshes(cx: number, cz: number, result: MeshResult): void {
    const key = worldKey(cx, cz);
    this.disposeMeshes(key);
    const list: THREE.Mesh[] = [];
    if (this.batcher) {
      for (const k of this.batchKinds) this.batcher.set(key, cx, cz, k, result[k]);
    }
    const passes: Array<[PassMesh, 'opaque' | 'cutout' | 'translucent']> = [
      [result.opaque, 'opaque'],
      [result.cutout, 'cutout'],
      [result.translucent, 'translucent'],
    ];
    for (const [p, kind] of passes) {
      if (p.indexCount === 0 || this.batchKinds.has(kind)) continue;
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('apos', releaseAfterUpload(new THREE.BufferAttribute(p.a, 4)));
      geo.setAttribute('auv', releaseAfterUpload(new THREE.BufferAttribute(p.b, 4)));
      geo.setAttribute('acolor', releaseAfterUpload(new THREE.BufferAttribute(p.c, 4, true)));
      geo.setIndex(releaseAfterUpload(new THREE.BufferAttribute(p.indices, 1)));
      // tight bounds from the mesh's real vertical extent, in chunk-local space
      const halfY = (p.maxY - p.minY) / 2;
      geo.boundingSphere = new THREE.Sphere(
        new THREE.Vector3(CHUNK_SIZE / 2, p.minY + halfY, CHUNK_SIZE / 2),
        Math.sqrt(2 * (CHUNK_SIZE / 2 + 1) ** 2 + halfY * halfY) + 0.5
      );
      const mesh = new THREE.Mesh(geo, this.materials[kind]);
      // Chunk-local geometry: the mesh position lets three.js sort opaque
      // chunks front-to-back (early-Z) and water back-to-front (blending).
      mesh.position.set(cx * CHUNK_SIZE, 0, cz * CHUNK_SIZE);
      mesh.matrixAutoUpdate = false;
      mesh.updateMatrix();
      // static: the (identity) group never moves, so skip per-frame traversal
      mesh.matrixWorld.copy(mesh.matrix);
      mesh.matrixWorldAutoUpdate = false;
      mesh.renderOrder = kind === 'translucent' ? 10 : kind === 'cutout' ? 5 : 0;
      this.group.add(mesh);
      list.push(mesh);

      if (kind !== 'translucent') {
        const caster = new THREE.Mesh(geo, kind === 'opaque' ? this.materials.depthOpaque : this.materials.depthCutout);
        caster.matrixAutoUpdate = false;
        caster.matrix.copy(mesh.matrix);
        caster.matrixWorld.copy(mesh.matrix); // shadow scene never auto-updates matrices
        caster.matrixWorldAutoUpdate = false;
        this.shadowGroup.add(caster);
        list.push(caster);
      }
    }
    this.meshes.set(key, list);
    this.version++;
  }

  private disposeMeshes(key: number): void {
    this.batcher?.remove(key);
    const list = this.meshes.get(key);
    if (!list) return;
    for (const m of list) {
      m.removeFromParent();
      m.geometry.dispose(); // shared by a mesh and its shadow twin; double dispose is harmless
    }
    this.meshes.delete(key);
    this.version++;
  }

  private unloadFar(): void {
    if (this.remote) return; // the server says when (unloadChunk)
    for (const [key, chunk] of this.world.chunks) {
      if (this.inRange(chunk.cx, chunk.cz)) continue;
      this.disposeMeshes(key);
      this.world.chunks.delete(key);
      this.remeshQueue.delete(key);
      // note: pending gen/mesh results for it are dropped on arrival
    }
  }

  /**
   * Switch the generated dimension: drop every loaded chunk and mesh, forget
   * in-flight jobs (their results are discarded by epoch) and reload around
   * the player on the next update(). The save must already point at `dim`.
   */
  setDimension(dim: DimensionId): void {
    this.dimension = dim;
    this.epoch++;
    for (const key of [...this.meshes.keys()]) this.disposeMeshes(key);
    this.world.chunks.clear();
    this.remeshQueue.clear();
    this.pendingGen.clear();
    this.pendingMesh.clear();
    this.pendingUploads.length = 0;
    this.lightDirty.clear();
    this.remoteQueue.clear();
    this.remoteStash.clear();
    this.remoteCancel.clear();
    this.partial.clear();
    this.hasCenter = false;
    this.settled = false;
    this.version++;
  }

  /** Edit a block and relight/remesh everything affected. */
  setBlock(wx: number, wy: number, wz: number, id: number): boolean {
    // the rules of the edit itself (door halves, supports, torches, rails, reshape) are shared with the server
    if (this.capture) this.captureAround(wx, wy, wz);
    else if (this.remote && this.sendEdit) { this.sendEdit(wx, wy, wz, id); return true; }
    const dirty = new Set<number>();
    const broken = editBlock(this.world, wx, wy, wz, id, this.editHooks, dirty);
    if (broken < 0) return false;

    for (const key of dirty) {
      const c = this.world.getChunk(keyCx(key), keyCz(key));
      if (c) {
        c.meshDirty = true;
        this.remeshQueue.add(key);
      }
    }
    this.settled = false;
    if (this.onBlockChange) {
      for (let i = 0; i <= broken; i++) this.onBlockChange(wx, wy + i, wz);
    }
    if (!this.batching) this.schedule(false); // start the remesh now rather than next frame
    return true;
  }

  // ---- remote mode ---------------------------------------------------------

  /** A chunk from the server (protocol.ts decodeChunk). Replaces any copy already held. */
  receiveChunk(dim: DimensionId, cx: number, cz: number, blocks: Uint16Array): void {
    if (!this.remote || dim !== this.dimension) return;
    const key = worldKey(cx, cz);
    this.lastChunkAt = performance.now();
    // a resend replaces the chunk; its mesh stays up until the new one is uploaded
    if (this.world.chunks.delete(key)) this.remeshQueue.delete(key);
    this.remoteStash.delete(key);
    this.remoteCancel.delete(key);
    this.remoteQueue.set(key, blocks);
    this.settled = false;
    this.schedule(false);
  }

  /** The server stopped keeping this chunk up to date: drop it and its mesh. */
  unloadChunk(cx: number, cz: number): void {
    const key = worldKey(cx, cz);
    this.remoteQueue.delete(key);
    this.remoteStash.delete(key);
    if (this.pendingGen.has(key)) this.remoteCancel.add(key);
    this.disposeMeshes(key);
    this.world.chunks.delete(key);
    this.remeshQueue.delete(key);
    this.partial.delete(key);
  }

  /** Chunks held or on their way in (remote mode: what the server has sent and not unloaded). */
  get remoteChunkCount(): number {
    let n = this.world.chunks.size + this.remoteQueue.size;
    for (const k of this.pendingGen) if (!this.remoteCancel.has(k) && !this.remoteQueue.has(k)) n++;
    return n;
  }

  /**
   * A fact from the server (`blocks`, an `ack` correction, a reverted
   * prediction): write the cell and relight. None of the edit rules run (the
   * server sends every cell they changed), nothing is recorded or echoed.
   */
  applyRemote(wx: number, wy: number, wz: number, id: number): void {
    if (wy < 0 || wy >= WORLD_HEIGHT) return;
    const cx = wx >> 4, cz = wz >> 4;
    const key = worldKey(cx, cz);
    const lx = wx & 15, lz = wz & 15;
    const idx = lx + lz * CHUNK_SIZE + wy * CHUNK_AREA;
    const queued = this.remoteQueue.get(key);
    if (queued) { queued[idx] = id; return; }
    const chunk = this.world.chunks.get(key);
    if (!chunk) {
      if (this.pendingGen.has(key) && !this.remoteCancel.has(key)) {
        const s = this.remoteStash.get(key);
        if (s) s.push(idx, id); else this.remoteStash.set(key, [idx, id]);
      }
      return;
    }
    const old = chunk.blocks[idx];
    if (old === id) return;
    chunk.blocks[idx] = id;
    chunk.solidCount += IS_SOLID[id] - IS_SOLID[old];
    this.settled = false;
    if (chunk.state < ChunkState.Lit) {
      // not stitched yet: its light is still chunk-local, so redo that
      chunk.light.fill(0);
      lightChunkLocal(chunk.blocks, chunk.light);
      return;
    }
    const dirty = new Set<number>();
    relightAfterEdit(this.world, wx, wy, wz, old, id, dirty);
    dirty.add(key);
    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) {
        if ((dx === 0 && dz === 0) || (dx !== 0 && lx !== (dx < 0 ? 0 : 15)) || (dz !== 0 && lz !== (dz < 0 ? 0 : 15))) continue;
        dirty.add(worldKey(cx + dx, cz + dz));
      }
    }
    for (const k of dirty) {
      const c = this.world.chunks.get(k);
      if (c) { c.meshDirty = true; this.remeshQueue.add(k); }
    }
    this.onBlockChange?.(wx, wy, wz);
    if (!this.batching) this.schedule(false);
  }

  /**
   * The player's own edit, applied at once with the full edit rules (as
   * setBlock). Returns [x, y, z, oldId, ...] for every cell it changed, so a
   * refused edit can be put back with revert().
   */
  predictBlock(wx: number, wy: number, wz: number, id: number): number[] {
    const snap: number[] = this.capture = [];
    try { this.setBlock(wx, wy, wz, id); } finally { this.capture = null; }
    const out: number[] = [];
    const seen = new Set<string>();
    for (let i = 0; i < snap.length; i += 4) {
      const k = snap[i] + ',' + snap[i + 1] + ',' + snap[i + 2];
      if (seen.has(k)) continue; // the first snapshot of a cell is its value before the edit
      seen.add(k);
      if (this.world.getBlock(snap[i], snap[i + 1], snap[i + 2]) !== snap[i + 3]) out.push(snap[i], snap[i + 1], snap[i + 2], snap[i + 3]);
    }
    return out;
  }

  /** Undo a predictBlock(): put its cells back. */
  revert(cells: readonly number[]): void {
    this.batchEdits(() => {
      for (let i = cells.length - 4; i >= 0; i -= 4) this.applyRemote(cells[i], cells[i + 1], cells[i + 2], cells[i + 3]);
    });
  }

  /** Snapshot every cell one editBlock() at (wx, wy, wz) can write: itself, a door's other half, the supported column above, the four reshaping neighbours. */
  private captureAround(wx: number, wy: number, wz: number): void {
    const cap = this.capture!;
    const w = this.world;
    const put = (x: number, y: number, z: number): void => { if (y >= 0 && y < WORLD_HEIGHT) cap.push(x, y, z, w.getBlock(x, y, z)); };
    put(wx, wy, wz);
    put(wx, wy - 1, wz);
    for (let y = wy + 1; y < WORLD_HEIGHT; y++) {
      put(wx, y, wz);
      if (y > wy + 1 && NEEDS_SUPPORT[w.getBlock(wx, y, wz)] !== 1) break;
    }
    put(wx + 1, wy, wz); put(wx - 1, wy, wz); put(wx, wy, wz + 1); put(wx, wy, wz - 1);
  }

  /**
   * Pending water updates are not saved, so water that was still flowing (or
   * draining) when its chunk was unloaded would stay frozen. Once a chunk and
   * its neighbours are back, report its saved edits again to wake that water.
   */
  private wakeEdits(chunk: Chunk): void {
    const edits = this.onBlockChange ? this.save?.editsFor(chunk.cx, chunk.cz) : undefined;
    if (!edits) return;
    const ox = chunk.cx * CHUNK_SIZE;
    const oz = chunk.cz * CHUNK_SIZE;
    for (let i = 0; i < edits.length; i += 2) {
      const idx = edits[i];
      this.onBlockChange!(ox + (idx & 15), Math.floor(idx / CHUNK_AREA), oz + ((idx >> 4) & 15));
    }
  }

  /** Run many setBlock calls (a water tick) and start their remeshes once at the end. */
  batchEdits(edit: () => void): void {
    this.batching = true;
    try {
      edit();
    } finally {
      this.batching = false;
    }
    if (!this.settled) this.schedule(false);
  }

  /** Can the block be edited right now: inside the world, in a chunk whose light is up to date? */
  canEdit(wx: number, wy: number, wz: number): boolean {
    if (wy < 0 || wy >= WORLD_HEIGHT) return false;
    const chunk = this.world.getChunk(wx >> 4, wz >> 4);
    return chunk !== undefined && chunk.state >= ChunkState.Lit;
  }

  /** Highest solid y at spawn column (requires the spawn chunk to be loaded). */
  spawnY(wx: number, wz: number): number {
    return this.world.highestSolid(wx, wz);
  }
}

/** Drop the CPU copy of a vertex/index buffer once it lives on the GPU. */
function releaseAfterUpload(attr: THREE.BufferAttribute): THREE.BufferAttribute {
  attr.onUpload(function (this: THREE.BufferAttribute) {
    (this as unknown as { array: unknown }).array = null;
  });
  return attr;
}
